// Ações do atendimento, com as mesmas mensagens e o "Desfazer" do protótipo.
import { api, ApiError, newKey } from '../api.ts';
import { dayLabel, first, nowLocal, hhmm, waLink } from '../lib/format.ts';
import { confirmationText, sendWhatsAppAfter } from '../lib/messages.ts';
import { useMe, usePros, useRefresh, type AgendaItem } from '../lib/data.ts';
import { useToast } from './toast.tsx';

type A = Pick<AgendaItem, 'id' | 'client' | 'phone' | 'status' | 'time'>;

export function useApptActions() {
  const toast = useToast();
  const refresh = useRefresh();
  const me = useMe().data;
  const pros = usePros().data ?? [];
  const status = (id: string, to: string, reason = '') => api.post(`/appointments/${id}/status`, { to, reason });

  async function run(fn: () => Promise<unknown>, ok: string, undo?: () => Promise<unknown>, kind: 'ok' | 'warn' = 'ok') {
    try {
      await fn();
      await refresh();
      toast(ok, kind, undo ? async () => {
        try { await undo(); await refresh(); toast('Desfeito.'); } catch (e) { toast(msg(e), 'warn'); }
      } : undefined);
      return true;
    } catch (e) {
      toast(msg(e), 'warn');
      await refresh();
      return false;
    }
  }

  return {
    arrived: (a: A) => run(() => status(a.id, 'arrived'), `${first(a.client)} chegou às ${hhmm(nowLocal().min)}.`, () => status(a.id, 'confirmed')),
    start: (a: A) => run(() => status(a.id, 'in_service'), `Atendimento de ${first(a.client)} iniciado.`, () => status(a.id, 'arrived')),
    confirm: (a: A) => run(() => status(a.id, 'confirmed'), `Horário de ${first(a.client)} confirmado.`),
    noShow: (a: A) => run(() => status(a.id, 'no_show'), `Falta de ${first(a.client)} registrada. O horário foi liberado.`, () => status(a.id, 'confirmed'), 'warn'),
    reopen: (a: A) => run(() => status(a.id, 'arrived', 'Apareceu depois da falta'), `${first(a.client)} está na recepção. A falta foi desfeita.`),
    cancel: (a: A, reason: string, salonFault = false, alsoNext = false) => run(() => api.post(`/appointments/${a.id}/cancel`, { reason, salonFault, alsoNext }), alsoNext ? `Horário de ${first(a.client)} e os próximos da série cancelados.` : `Horário de ${first(a.client)} cancelado. O horário ficou livre.`),
    depositReceived: (a: A) => run(() => api.post(`/appointments/${a.id}/deposit`, { action: 'received' }), `Sinal de ${first(a.client)} recebido e lançado no caixa.`),
    depositExtend: (a: A) => run(() => api.post(`/appointments/${a.id}/deposit`, { action: 'extend', hours: 1 }), `Mais 1 hora para o sinal de ${first(a.client)}.`),
    depositRelease: (a: A) => run(() => api.post(`/appointments/${a.id}/deposit`, { action: 'release' }), `Horário de ${first(a.client)} liberado.`, undefined, 'warn'),
    reschedule: (a: A, date: string, time: string, professionalId?: string, back?: { date: string; time: string; professionalId: string }) =>
      run(() => api.post(`/appointments/${a.id}/reschedule`, { date, time, professionalId }), `${first(a.client)} remarcada para ${time}.`,
        back ? () => api.post(`/appointments/${a.id}/reschedule`, back) : undefined),
    // pede a confirmação com a mensagem que a dona editou em Mensagens (a mesma do cartão do horário e da tela Lembretes)
    askConfirmation: (a: AgendaItem) => {
      if (!a.phone) { toast(`${first(a.client)} não tem celular cadastrado.`, 'warn'); return; }
      const vars = { nome: first(a.client), servico: a.service, profissional: first(pros.find((p) => p.id === a.professionalId)?.name ?? ''), dia: dayLabel(a.date, nowLocal().date).toLowerCase(), hora: a.time };
      return sendWhatsAppAfter(a.phone, () => confirmationText(me?.salon.settings ?? {}, vars, async () => (await api.post<{ path: string }>(`/appointments/${a.id}/link`)).path))
        .catch((e) => toast(msg(e), 'warn'));
    },
    // WhatsApp: abre a conversa com o texto pronto (o envio é da pessoa, no WhatsApp dela)
    remind: (a: A & { whatsapp?: boolean }, text?: string) => {
      if (!a.phone) { toast(`${first(a.client)} não tem celular cadastrado.`, 'warn'); return; }
      window.open(waLink(a.phone, text ?? `Oi, ${first(a.client)}! Confirma seu horário às ${a.time}?`), '_blank', 'noopener');
    },
    newKey,
  };
}

export function msg(e: unknown): string {
  return e instanceof ApiError ? e.message : 'Sem conexão. Nada foi gravado; tente de novo.';
}
